import React, { useState, useEffect } from "react";

const SessionTimeDistributionChart = ({ recording }) => {
  const [sessionData, setSessionData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!recording) return;
        const transcript = recording.transcript.transcript;
        const userObjects = recording.user_objects;

        // Calculate speaking time for each user
        const userSpeakingTime = {};
        const silencePeriods = [];

        // Initialize user data
        Object.keys(userObjects).forEach((userId) => {
          userSpeakingTime[userId] = 0;
        });

        // Sort transcript by start time
        const sortedTranscript = transcript.sort((a, b) => a.start - b.start);

        // Calculate speaking time and detect silence
        for (let i = 0; i < sortedTranscript.length; i++) {
          const item = sortedTranscript[i];
          const speakingDuration = item.end - item.start;

          if (userSpeakingTime.hasOwnProperty(item.speakerId)) {
            userSpeakingTime[item.speakerId] += speakingDuration;
          }

          // Check for silence (gap > 5 seconds between this and next item)
          if (i < sortedTranscript.length - 1) {
            const nextItem = sortedTranscript[i + 1];
            const gap = nextItem.start - item.end;
            if (gap > 5) {
              silencePeriods.push({
                start: item.end,
                end: nextItem.start,
                duration: gap,
              });
            }
          }
        }

        // Calculate total silence time
        const totalSilenceTime = silencePeriods.reduce(
          (sum, period) => sum + period.duration,
          0
        );

        // Create session data with user names and personas
        const processedData = {};

        Object.keys(userSpeakingTime).forEach((userId, index) => {
          const user = userObjects[userId];
          const totalSeconds = userSpeakingTime[userId];

          // Skip users with 0 talk time
          if (totalSeconds <= 0) {
            return;
          }

          // Skip unknown speakers
          if (!user || !user.persona || user.persona === "Unknown" || !user.name) {
            return;
          }

          // Round to nearest minute
          const minutes = Math.round(totalSeconds / 60);

          // Assign colors based on persona
          let color;
          if (user.persona === "Student") {
            color = index === 0 ? "#3B82F6" : "#60A5FA"; // Blue variants for students
          } else if (user.persona === "Guest") {
            color = "#EF4444"; // Red for teacher/guest
          } else {
            color = "#8B5CF6"; // Purple for admin
          }

          processedData[userId] = {
            label: `${user.name} (${user.persona})`,
            minutes,
            color,
          };
        });

        // Add silence data
        const silenceMinutes = Math.round(totalSilenceTime / 60);

        processedData["silence"] = {
          label: "Silence",
          minutes: silenceMinutes,
          color: "#10B981", // Green
        };

        setSessionData(processedData);
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading data:", error);
        setIsLoading(false);
      }
    };

    loadData();
  }, [recording]);

  // Calculate total time and percentages
  const getTotalMinutes = () => {
    return Object.values(sessionData).reduce((total, item) => {
      return total + item.minutes;
    }, 0);
  };

  const totalMinutes = getTotalMinutes();

  const getBarWidth = (minutes) => {
    return (minutes / totalMinutes) * 100;
  };

  const formatTime = (minutes) => {
    if (minutes === 0) {
      return "<1 min";
    }
    return `${minutes} min`;
  };

  if (isLoading) {
    return (
      <div className="bg-gradient-to-r from-blue-400 to-blue-500 rounded-2xl p-8 text-white">
        <div className="bg-white/95 backdrop-blur rounded-xl p-6 text-gray-800">
          <h2 className="text-xl font-medium mb-6 text-blue-600">
            Loading talk time distribution...
          </h2>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-6 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const barItems = Object.entries(sessionData).map(([key, data]) => ({
    key,
    label: data.label,
    data,
  }));

  return (
    <div className="bg-gradient-to-r from-blue-400 to-blue-500 rounded-2xl p-8 text-white">
      <div className="bg-white/95 backdrop-blur rounded-xl p-6 text-gray-800">
        <h2 className="text-xl font-medium mb-6 text-blue-600">
          Here is the distribution of talk time in this lesson.
        </h2>

        <div className="space-y-4">
          {barItems.map((item, index) => (
            <div key={item.key} className="flex items-center">
              {/* Label */}
              <div
                className="w-32 text-sm font-medium"
                style={{ color: item.data.color }}
              >
                {item.label}
              </div>

              {/* Bar container */}
              <div className="flex-1 mx-4">
                <div className="h-6 bg-gray-100 rounded-md relative overflow-hidden">
                  <div
                    className="h-full rounded-md transition-all duration-500 ease-out"
                    style={{
                      backgroundColor: item.data.color,
                      width: `${getBarWidth(item.data.minutes)}%`,
                    }}
                  />
                </div>
              </div>

              {/* Time label */}
              <div
                className="w-20 text-right text-sm font-medium"
                style={{ color: item.data.color }}
              >
                {formatTime(item.data.minutes)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SessionTimeDistributionChart;
